import { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import UploadDialog from "@/components/UploadDialog";
import PhotoCard from "@/components/PhotoCard";
import { api, photoUrl } from "@/lib/api";
import { FolderHeart, ChevronLeft } from "lucide-react";

export default function Albums() {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [pets, setPets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [photos, setPhotos] = useState([]);

  const loadAlbums = async () => {
    const [a, p] = await Promise.all([api.get("/albums"), api.get("/pets")]);
    setAlbums(a.data);
    setPets(p.data);
  };

  useEffect(() => { loadAlbums(); }, []);

  const openAlbum = async (name) => {
    setSelected(name);
    const { data } = await api.get("/photos", { params: { album: name } });
    setPhotos(data);
  };

  return (
    <div className="min-h-screen">
      <Nav onUpload={() => setUploadOpen(true)} />
      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-10 relative z-10">
        {!selected ? (
          <>
            <div className="mb-8">
              <div className="font-hand text-2xl text-[#E76F51] mb-1">grouped by theme</div>
              <h1 className="font-display text-5xl font-bold text-[#2D232E] tracking-tight">Albums</h1>
            </div>
            {albums.length === 0 ? (
              <div className="rounded-3xl bg-white border border-[#EAE2D6] p-14 text-center warm-shadow">
                <div className="w-16 h-16 rounded-full bg-[#F4EFE6] flex items-center justify-center mx-auto mb-4">
                  <FolderHeart className="w-7 h-7 text-[#E76F51]" />
                </div>
                <h3 className="font-display text-2xl font-bold text-[#2D232E]">No albums yet</h3>
                <p className="text-[#6A606C] mt-1">Set an album name when uploading a photo to group them.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {albums.map((a) => (
                  <button
                    key={a.name}
                    onClick={() => openAlbum(a.name)}
                    className="rounded-3xl bg-white border border-[#EAE2D6] p-4 warm-shadow hover:-translate-y-1 transition-transform text-left"
                  >
                    <div className="w-full aspect-[4/3] rounded-2xl overflow-hidden mb-3 bg-[#F4EFE6]">
                      <img src={photoUrl(a.cover_photo_id)} alt={a.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="font-display font-semibold text-lg text-[#2D232E] truncate">{a.name}</div>
                    <div className="text-sm text-[#6A606C]">{a.count} photo{a.count === 1 ? "" : "s"}</div>
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <button
              onClick={() => setSelected(null)}
              className="inline-flex items-center gap-1 text-[#E76F51] font-semibold mb-4 hover:underline"
            >
              <ChevronLeft className="w-4 h-4" /> Back to albums
            </button>
            <div className="mb-8">
              <div className="font-hand text-2xl text-[#E76F51] mb-1">album</div>
              <h1 className="font-display text-5xl font-bold text-[#2D232E] tracking-tight">{selected}</h1>
            </div>
            <div className="columns-2 md:columns-3 lg:columns-4 gap-6">
              {photos.map((p, i) => (
                <PhotoCard key={p.id} photo={p} index={i} pets={pets} onUpdate={() => openAlbum(selected)} onDelete={() => openAlbum(selected)} onCoverChanged={() => openAlbum(selected)} />
              ))}
            </div>
          </>
        )}
      </main>
      <UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} onUploaded={loadAlbums} />
    </div>
  );
}
